// Tesorería · Pago de Clientes (docs/108 B4): contratos y lecturas de la cuenta corriente y los cobros.
// Toda clave cuelga de ['emp', e, 'cobranza', …]: un cobro, una aplicación o una reversa la invalida.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

/** Un pago de un documento, por medio (trazabilidad: con qué se pagó, mixto o no). */
export interface PagoDeDocumento {
  reciboId: string;
  recibo: string;
  fecha: string;
  medio: string;
  monto: string;
  terminal: string | null;
  referencia: string | null;
  numeroDocumento: string | null;
  banco: string | null;
  fechaDocumento: string | null;
}

/** «EFV 10.000 + ATB 2.760» (código Random del medio y monto). */
export const CODIGO_MEDIO: Record<string, string> = {
  EFECTIVO: 'EFV',
  TRANSFERENCIA: 'ATB',
  DEBITO: 'TDV',
  CREDITO: 'TJV',
  CHEQUE: 'CHV',
};
export const NOMBRE_MEDIO_PAGO: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA: 'Transferencia',
  DEBITO: 'Tarjeta débito',
  CREDITO: 'Tarjeta crédito',
  CHEQUE: 'Cheque',
};
/** Detalle legible de un pago: «Transferencia op. 7788 (20-10-2026)», «Cheque N° 123 Banco Estado». */
export const detallePago = (p: {
  medio: string;
  terminal?: string | null;
  referencia?: string | null;
  numeroDocumento?: string | null;
  banco?: string | null;
}) =>
  [
    NOMBRE_MEDIO_PAGO[p.medio] ?? p.medio,
    p.terminal === 'LINK_PAGO' ? 'link de pago' : p.terminal === 'EN_LINEA' ? 'en línea' : '',
    p.referencia ? `op. ${p.referencia}` : '',
    p.numeroDocumento ? `N° ${p.numeroDocumento}` : '',
    p.banco ?? '',
  ]
    .filter(Boolean)
    .join(' · ');

export interface DocumentoConSaldo {
  documentoId: string;
  clase: 'VENTA' | 'NC' | 'ND';
  tipoDte: number;
  folio: string | null;
  fechaEmision: string;
  fechaVencimiento: string;
  montoTotal: string;
  estado: string;
  notaId: string;
  notaNumeroVisible: string;
  clienteEntidadId: string;
  clienteRazonSocial: string;
  clienteRut: string | null;
  vendedorId: string | null;
  condicionPago: string;
  notasCredito: string;
  neto: string;
  aplicado: string;
  /** Lo que cubre el excedente de una NC sobre otro documento ya pagado del cliente. */
  compensado?: string;
  saldo: string;
  diasVencido: number;
  /** Con qué se pagó (medio por medio). */
  pagos?: PagoDeDocumento[];
}

export interface ReciboCobro {
  reciboId: string;
  numeroVisible: string;
  clienteEntidadId: string;
  destino: string;
  notaId: string | null;
  notaNumero: string | null;
  total: string;
  aplicado: string;
  saldoAFavor: string;
  estado: string;
  version: number;
  creadoEn: string;
  creadoPor?: string;
  /** Teneduría de pagos: quién cobró, para qué cliente y en qué turno de caja. */
  creadoPorNombre?: string | null;
  clienteRazonSocial?: string | null;
  clienteRut?: string | null;
  turno?: { numero: string; estado: string; punto: string } | null;
  anuladoEn?: string | null;
  observacion: string | null;
  motivoAnulacion: string | null;
  medios: Array<{
    medio: string;
    monto: string;
    banco: string | null;
    numeroDocumento: string | null;
    fechaDocumento: string | null;
    referencia: string | null;
    autorizacion: string | null;
    terminal?: string | null;
    cuotas?: number | null;
    linea?: number;
  }>;
  aplicaciones: Array<{
    aplicacionId: string;
    tipo: 'APLICACION' | 'REVERSA';
    documentoId: string;
    tipoDte: number | null;
    folio: string | null;
    monto: string;
    origen: string;
    aplicadoEn: string;
    reversada: boolean;
    /** Con qué medio(s) del recibo se pagó este documento. */
    medios?: Array<{ linea: number; medio: string; monto: string }>;
  }>;
}

export interface Cartola {
  clienteEntidadId: string;
  saldoInicial: string;
  saldoFinal: string;
  saldoAFavor: string;
  movimientos: Array<{
    fecha: string;
    tipo: 'CARGO' | 'NOTA_CREDITO' | 'PAGO';
    descripcion: string;
    documentoId: string | null;
    reciboId: string | null;
    /** Columnas como la cartola de Random: código del tipo, número, NV, vencimiento, detalle. */
    td?: string;
    numero?: string;
    nv?: string | null;
    vence?: string | null;
    detalle?: string;
    cargo: string;
    abono: string;
    saldo: string;
  }>;
  /** Lo que se debe hoy, documento por documento (del ERP y heredado de Random al corte). */
  pendientes?: Array<{
    td: string;
    numero: string;
    nv: string | null;
    emision: string;
    vence: string | null;
    diasVencido: number;
    valor: string;
    saldo: string;
    heredado: boolean;
    pagos?: PagoDeDocumento[];
  }>;
  /** Documentos pagados y después anulados enteros por NC: lo pagado no se compensa solo. */
  porRevisar?: Array<{ td: string; numero: string; nv: string | null; excedente: string }>;
}

export type Tramo = 'corriente' | '1-30' | '31-60' | '61-90' | '91+';
export interface ResumenCliente {
  clienteEntidadId: string;
  cliente: string;
  rut: string | null;
  documentos: number;
  saldo: string;
  vencido: string;
  saldoAFavor: string;
  neto: string;
  tramos: Record<Tramo, string>;
  vencimientoMasAntiguo: string | null;
}

export const clavesCobranza = {
  cobranza: (e: string) => ['emp', e, 'cobranza'] as const,
  documentos: (e: string, f: object) => ['emp', e, 'cobranza', 'documentos', f] as const,
  recibos: (e: string, f: object) => ['emp', e, 'cobranza', 'recibos', f] as const,
  cartola: (e: string, c: string, f: object) => ['emp', e, 'cobranza', 'cartola', c, f] as const,
  resumen: (e: string) => ['emp', e, 'cobranza', 'resumen'] as const,
};

/** Deuda conocida del cliente (cartera heredada al corte + ERP − saldo a favor), la del crédito. */
export interface ExposicionCxc {
  fuente: string;
  deuda: string;
  vencidos: string;
  heredado: string;
  fechaCorte: string;
}

/** `exposicion: null` mientras la empresa no tenga cargada su cartera heredada (D-118). */
export function useExposicionCxc(empresaId: string, clienteId: string | null, habilitado = true) {
  return useQuery({
    queryKey: [...clavesCobranza.cobranza(empresaId), 'exposicion', clienteId] as const,
    queryFn: () =>
      api.get<{ exposicion: ExposicionCxc | null }>('/tesoreria/cxc/exposicion', {
        query: { empresaId, clienteEntidadId: clienteId ?? undefined },
      }),
    enabled: habilitado && !!clienteId,
  });
}

export function useDocumentosCxc(
  empresaId: string,
  f: {
    clienteEntidadId?: string | undefined;
    abiertos?: boolean;
    folio?: string | undefined;
    vencenDesde?: string | undefined;
    vencenHasta?: string | undefined;
  },
  habilitado = true,
) {
  return useQuery({
    queryKey: clavesCobranza.documentos(empresaId, f),
    queryFn: () =>
      api.get<DocumentoConSaldo[]>('/tesoreria/cxc/documentos', {
        query: {
          empresaId,
          clienteEntidadId: f.clienteEntidadId,
          abiertos: f.abiertos ? 'true' : undefined,
          folio: f.folio,
          vencenDesde: f.vencenDesde,
          vencenHasta: f.vencenHasta,
        },
      }),
    enabled: habilitado,
  });
}

export function useRecibos(
  empresaId: string,
  f: {
    clienteEntidadId?: string | undefined;
    desde?: string | undefined;
    hasta?: string | undefined;
    soloConSaldo?: boolean;
    /** Teneduría de pagos: N° de recibo o de nota, medio y estado. */
    texto?: string | undefined;
    medio?: string | undefined;
    estado?: 'VIGENTE' | 'ANULADO' | undefined;
  },
  habilitado = true,
) {
  return useQuery({
    queryKey: clavesCobranza.recibos(empresaId, f),
    queryFn: () =>
      api.get<ReciboCobro[]>('/tesoreria/cobros', {
        query: {
          empresaId,
          clienteEntidadId: f.clienteEntidadId,
          desde: f.desde,
          hasta: f.hasta,
          soloConSaldo: f.soloConSaldo ? 'true' : undefined,
          texto: f.texto || undefined,
          medio: f.medio || undefined,
          estado: f.estado || undefined,
        },
      }),
    enabled: habilitado,
  });
}

export function useCartola(
  empresaId: string,
  clienteId: string | null,
  f: { desde?: string; hasta?: string },
) {
  return useQuery({
    queryKey: clavesCobranza.cartola(empresaId, clienteId ?? '', f),
    queryFn: () =>
      api.get<Cartola>('/tesoreria/cxc/cartola', {
        query: { empresaId, clienteEntidadId: clienteId ?? '', desde: f.desde, hasta: f.hasta },
      }),
    enabled: !!clienteId,
  });
}

export function useResumenCxc(empresaId: string) {
  return useQuery({
    queryKey: clavesCobranza.resumen(empresaId),
    queryFn: () => api.get<ResumenCliente[]>('/tesoreria/cxc/resumen', { query: { empresaId } }),
  });
}

// ---------------------------------------------------------------- cartola por vendedor

/** El mismo criterio del control de crédito: vencido más allá de los días de morosidad de la ficha. */
export type EstadoCuenta = 'EN_MORA' | 'VENCIDO_TOLERADO' | 'AL_DIA' | 'SIN_DEUDA';

export interface PendienteDeCartera {
  documentoId: string;
  td: string;
  numero: string;
  nv: string | null;
  emision: string;
  vence: string;
  diasVencido: number;
  valor: string;
  saldo: string;
  heredado: boolean;
}

/** La cuenta corriente de un cliente de la cartera del vendedor (su ficha lo asigna). */
export interface CuentaDeCartera {
  clienteEntidadId: string;
  cliente: string;
  rut: string | null;
  vendedorId: string | null;
  vendedorCodigo: string | null;
  vendedorNombre: string | null;
  condicionPago: string;
  plazoDias: number;
  lineaCredito: string | null;
  diasMorosidad: number;
  bloqueado: boolean;
  estado: EstadoCuenta;
  documentos: number;
  saldo: string;
  vencido: string;
  tramos: Record<Tramo, string>;
  saldoAFavor: string;
  neto: string;
  chequesProtestados: string;
  diasMaxVencido: number;
  vencimientoMasAntiguo: string | null;
  pendientes: PendienteDeCartera[];
}

export interface TotalVendedor {
  vendedorId: string | null;
  codigo: string | null;
  nombre: string;
  cartera: number;
  clientes: number;
  porEstado: Record<EstadoCuenta, number>;
  saldo: string;
  vencido: string;
  tramos: Record<Tramo, string>;
  saldoAFavor: string;
  neto: string;
}

export interface CartolaPorVendedor {
  fecha: string;
  vendedores: TotalVendedor[];
  clientes: CuentaDeCartera[];
}

export function useCartolaPorVendedor(
  empresaId: string,
  f: { vendedorId: string | null; incluirSinDeuda: boolean },
) {
  return useQuery({
    queryKey: [...clavesCobranza.cobranza(empresaId), 'por-vendedor', f] as const,
    queryFn: () =>
      api.get<CartolaPorVendedor>('/tesoreria/cxc/por-vendedor', {
        query: {
          empresaId,
          vendedorId: f.vendedorId ?? undefined,
          incluirSinDeuda: f.incluirSinDeuda ? 'true' : undefined,
        },
      }),
  });
}

export const NOMBRE_TIPO_DTE: Record<number, string> = {
  // 0109: el vale de venta (interno, no es DTE) en la cuenta corriente y la caja.
  0: 'Vale',
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};
